import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MutationStatus } from 'react-query';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  ApiButton,
  Box,
  ContentTemplate,
  ErrorInfo,
  Grid,
  SectionHeader,
  Typography,
} from 'web-library';
import { candidateService, KeyValueResponse } from '../../../../api';
import { putUploadFileToS3 } from '../../../../api/candidate/resit/@coordinator/api-services/api';
import { WindowPeriodByExamLevelResponse } from '../../../../api/candidate/resit/@coordinator/api-services/models';
import { PreSignUploadURLResponse } from '../../../../api/candidate/resit/@coordinator/model';
import { useGetWindowPeriodByExamLevel } from '../../../../api/candidate/resit/@coordinator/queries-services/queries';
import { useAuditApplyResitRequestMutation } from '../../../../api/candidate/result/@candidate/queries-services/queries';
import BulkUpload from '../../../../components/BulkUpload';
import WindowPeriodInformation from '../../../../components/cards/WindowPeriodInformation';
import ExamLevelOption from '../../../../components/select/ExamLevel';
import { useTriggerMutate } from '../../../../hooks/query';
import { useUserDetails } from '../../../../hooks/userDetails';
import RouteKey from '../../../../routes/key';
import { useResitNavigation } from '../../../../routes/ResitRoute';
import {
  UpdateBankTransferOption,
  UpdateCandidateResitRegistration,
} from '../../../../store/resitSlice';
import UploadOptionAction from './buttons/UploadOptionAction';

const { error: errorRoute } = RouteKey.child;
const { invalid: invalidRoute } = RouteKey.error.child;

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  menuItem: {
    marginBottom: theme.spacing(4),
  },
  submitForm: {
    width: '100%',
  },
  errorContainer: {
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  wrapper: {
    position: 'absolute',
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  uploadContainer: {
    width: '100%',
  },
  newRegistration: {
    marginBottom: theme.spacing(4),
  },
  examLevelField: {
    marginBottom: theme.spacing(4),
  },
}));
const tryAgainString = 'Please try again.';

const useEligibility = (examLevelSelectValue: KeyValueResponse, callback: () => void) => {
  const dispatch = useDispatch();
  const { accessToken } = useResitNavigation();
  const history = useHistory();
  const [eligible, setEligible] = useState<WindowPeriodByExamLevelResponse | undefined>();

  const { refetch: checkEligibility } = useGetWindowPeriodByExamLevel(
    // to retrieve window period by selected exam level
    examLevelSelectValue?.code || '',
    {
      cacheTime: 0,
      enabled: false,
      onSuccess: (response) => {
        if (response && response.timeWindow) {
          setEligible(response);
        } else if (response && response.code && response.code === '1600') {
          setEligible(undefined);
          callback();
        }
      },
      onError: (err) => {
        setEligible(undefined);
      },
    }
  );

  const { isSuccess: byPassSuccess, mutate } = candidateService.resit.all.usePostValidateAccessLink(
    (res) => {
      dispatch(UpdateBankTransferOption(res.data.bankTransferEnabled));
    },
    (err) => {
      // fail mean invalid link, redirect
      history.push(`/${errorRoute}/${invalidRoute}`);
    }
  );

  const callMutation = useCallback(() => {
    if (accessToken) {
      mutate({
        token: accessToken,
      });
    }
  }, [mutate, accessToken]);
  const setTriggerMutate = useTriggerMutate(callMutation);

  useEffect(() => {
    if (accessToken) {
      setTriggerMutate((prev) => prev + 1);
    }
  }, [accessToken, setTriggerMutate]);

  return { byPassSuccess, eligible, checkEligibility };
};

const useCoordinatorResitRegister = () => {
  const dispatch = useDispatch();
  const { state } = useLocation<any>();

  const { accessToken } = useResitNavigation();

  const [uploadFile, setUploadFile] = useState<File[]>([]);
  const [uploadOption, setUploadOption] = useState(true);
  const [preSignKey, setPreSignKey] = useState('');
  const [dropZoneKey, setDropZoneKey] = useState(0);
  const [uploadErrors, setUploadErrors] = useState('');
  const [examLevelSelectValue, setExamLevelSelectValue] = useState<KeyValueResponse>(
    state?.selectedExamLevel
  );
  const [eligibleError, setEligibleError] = useState<boolean>(false);
  const [processStatus, setProcessStatus] = useState<MutationStatus>('idle');
  const { toCoordinatorResitFlow } = useResitNavigation();

  const { handleSubmit, reset } = useForm();
  const { data: currentUserDetails } = useUserDetails();
  const history = useHistory();

  const { byPassSuccess, eligible, checkEligibility } = useEligibility(examLevelSelectValue, () =>
    setEligibleError(true)
  );

  const getUploadStatus = candidateService.resit.coordinator.usePostFileStatus(
    // to retrieve upload status from s3
    (err) => {
      handleErrors(tryAgainString);
    }
  );

  const { refetch } = candidateService.resit.coordinator.useGetCSVDownloadUrl();

  /** put uploaded document to S3 service
   * */
  const uploadToS3 = async (data: PreSignUploadURLResponse) => {
    try {
      const s3FormData = new FormData();
      s3FormData.append(data.key, uploadFile[0], data.key);
      const uploadedFile: File = uploadFile[0];
      const response = await putUploadFileToS3(
        import.meta.env.PROD ? uploadedFile : s3FormData,
        data.presignedUrl
      );
      return Promise.resolve(response);
    } catch (errors) {
      handleErrors('Please try again');
      return Promise.reject(errors);
    }
  };

  const getPreSignUploadUrl = candidateService.resit.coordinator.useGetPreSignUploadUrl({
    // to retrieve s3 upload url
    onSuccess: async (data) => {
      if (uploadFile && uploadFile.length > 0) {
        const s3Response = await uploadToS3(data);
        if (s3Response && s3Response.status === 200) {
          const fileStatus = await getUploadStatus.mutateAsync({
            key: data.key,
            fileName: uploadFile[0].name,
          });
          if (fileStatus.status === 200) {
            setPreSignKey(data.key);
          } else {
            handleErrors(tryAgainString);
          }
        } else {
          handleErrors(tryAgainString);
        }
      }
    },
    onError: (response) => {
      handleErrors('Please try again');
    },
  });

  const postValidUploadFile = candidateService.resit.coordinator.usePostValidateUploadFile(
    // to validate upload file
    async (res) => {
      setPreSignKey('');
      dispatch(
        UpdateCandidateResitRegistration({
          candidateDetails: res.data,
          examLevel: examLevelSelectValue,
        })
      );
      nextPage();
    },
    async (err) => {
      setPreSignKey('');
      setProcessStatus('error');
      if (err && err.response) {
        if (err.response.data && err.response.data.code === '1006') {
          handleErrors(err.response.data.message);
        } else {
          handleErrors(tryAgainString);
        }
      } else {
        handleErrors(tryAgainString);
      }
    }
  );

  candidateService.resit.coordinator.useGetFileScanningStatus(preSignKey, {
    // to watch upload file scanning result
    refetchInterval: 5000,
    cacheTime: 0,
    enabled: !!preSignKey,
    onSuccess: (data) => {
      if (data.status === 'SUCCESS') {
        setProcessStatus('success');
        postValidUploadFile.mutate({
          key: preSignKey,
          examLevel: examLevelSelectValue?.code || '',
        });
      } else if (data.status === 'FAILED') {
        handleErrors('Please try to upload again.');
      }
    },
    onError: (err) => {
      handleErrors(tryAgainString);
    },
  });

  const onUploadOptionChange = () => {
    // to handle upload option change event (Template or Manual)
    setUploadErrors('');
    setUploadOption(!uploadOption);
  };

  const onFileUpload = async (files: File[] | []) => {
    setUploadFile(files);
  };

  const handleErrors = (errorMessage: any) => {
    setProcessStatus('error');
    reset();
    setUploadErrors(errorMessage);
  };

  const onDownloadTemplate = async () => {
    // to handle download template event
    const refetchResponse = await refetch();
    if (refetchResponse.isSuccess) {
      const newWindow = window.open(refetchResponse.data.url, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.opener = null;
      }
    }
  };

  const applyResit = useAuditApplyResitRequestMutation(() => {
    if (!uploadOption) {
      history.push(toCoordinatorResitFlow, {
        selectedRegisterOption: '',
        selectedExamLevel: examLevelSelectValue,
      });
    }
  });

  const onSubmit = async () => {
    // to submit form data
    if (uploadOption) {
      if (uploadFile.length === 0) {
        setUploadErrors('Please select file to upload.');
      } else {
        applyResit.mutate();
        setProcessStatus('loading');
        await getPreSignUploadUrl.refetch();
      }
    } else {
      dispatch(
        UpdateCandidateResitRegistration({ candidateDetails: [], examLevel: examLevelSelectValue })
      );
      applyResit.mutate();
      nextPage();
    }
  };

  const nextPage = () =>
    history.push(toCoordinatorResitFlow, {
      selectedRegisterOption: '',
      selectedExamLevel: examLevelSelectValue,
    });

  const onExamLevelOptionChange = async (selectedOption: any) => {
    // to handle exam level options change
    setEligibleError(false);
    setExamLevelSelectValue(selectedOption);
  };

  const onUploadFileDelete = async (file: File) => {
    // to handle file delete
    const fileIndex = uploadFile.findIndex((f) => f.name === file.name && f.size === file.size);
    if (fileIndex >= 0) {
      uploadFile.splice(fileIndex, 1);
      setDropZoneKey(dropZoneKey + 1);
    }
  };

  useEffect(() => {
    if (state && !accessToken) {
      setEligibleError(false);
      checkEligibility();
      history.replace(history.location.pathname, undefined);
    }
    if (examLevelSelectValue && !accessToken) {
      checkEligibility();
    }
  }, [checkEligibility, history, state, examLevelSelectValue, accessToken]);

  return {
    uploadFile,
    uploadOption,
    dropZoneKey,
    uploadErrors,
    examLevelSelectValue,
    byPassSuccess,
    eligible,
    eligibleError,
    processStatus,
    currentUserDetails,
    handleSubmit,
    onUploadOptionChange,
    onFileUpload,
    onDownloadTemplate,
    onSubmit,
    onExamLevelOptionChange,
    onUploadFileDelete,
  };
};

const CoordinatorResitRegister = () => {
  const { classes: useClasses } = useStyles();
  const { t } = useTranslation();

  const {
    uploadFile,
    uploadOption,
    dropZoneKey,
    uploadErrors,
    examLevelSelectValue,
    byPassSuccess,
    eligible,
    eligibleError,
    processStatus,
    currentUserDetails,
    handleSubmit,
    onUploadOptionChange,
    onFileUpload,
    onDownloadTemplate,
    onSubmit,
    onExamLevelOptionChange,
    onUploadFileDelete,
  } = useCoordinatorResitRegister();

  return (
    <div className={useClasses.root} data-testid="resit-register">
      <form className={useClasses.submitForm} onSubmit={handleSubmit(onSubmit)}>
        <ContentTemplate isContinuable>
          <SectionHeader
            title={`Resit Registration for ${currentUserDetails?.data.organisationName || ''}`}
          />
          <Typography variant="h3" className={useClasses.newRegistration}>
            {t('resit->>CoordinatorResit.Select Exam Level')}
          </Typography>

          <Box className={useClasses.examLevelField}>
            <ExamLevelOption
              // label={t('resit->>CoordinatorResit.Select Exam Level for Registration')}
              onChange={(e) => {
                onExamLevelOptionChange(e);
              }}
              value={examLevelSelectValue?.code}
            />
          </Box>
        </ContentTemplate>

        {eligibleError && (
          <Box className={useClasses.errorContainer}>
            <ErrorInfo
              label={t(
                'resit->>RegisterResit.Your request is not success, please try again later.'
              )}
            />
          </Box>
        )}

        <Box className={useClasses.uploadContainer}>
          {(byPassSuccess || (eligible && eligible.timeWindowOpen)) && !!examLevelSelectValue && (
            <ContentTemplate continuous isDividerTop>
              <UploadOptionAction
                uploadOptionValue={uploadOption}
                onUploadOptionChange={onUploadOptionChange}
              />
              {uploadOption && (
                <BulkUpload
                  onDownloadTemplate={onDownloadTemplate}
                  onChange={(file) => onFileUpload(file)}
                  onDelete={onUploadFileDelete}
                  initialFiles={uploadFile}
                  key={dropZoneKey}
                  xs={12}
                  sm={12}
                  md={10}
                  lg={10}
                />
              )}
              {uploadErrors && (
                <Grid xs={12} className={useClasses.errorContainer}>
                  <ErrorInfo label={uploadErrors} />
                </Grid>
              )}
              <Grid container>
                <Grid xs={12} sm={12} md={5}>
                  <ApiButton
                    id="button-resitRegister-startRegistration"
                    status={processStatus}
                    fullWidth
                  >
                    Start Registration Process
                  </ApiButton>
                </Grid>
              </Grid>
            </ContentTemplate>
          )}
        </Box>

        <ContentTemplate continuous type="full">
          {eligible && eligible.timeWindow && !eligible.timeWindowOpen && (
            <WindowPeriodInformation page="coordinator" timeWindow={eligible.timeWindow} />
          )}
        </ContentTemplate>
      </form>
    </div>
  );
};

export default CoordinatorResitRegister;
